/**
 * NotificationContext - Shared notification state for badges
 * Manages connection requests count and new feed posts count
 */

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import ConnectionService from '../services/agentProfile/ConnectionService';
import socialFeedService from '../services/socialFeedService';

const NotificationContext = createContext(null);

// No automatic polling — badges are refreshed on-demand when visiting Lending Network pages

export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

export const NotificationProvider = ({ children }) => {
    const [pendingConnectionsCount, setPendingConnectionsCount] = useState(0);
    const [newFeedPostsCount, setNewFeedPostsCount] = useState(0);
    const connectionCallbackRef = useRef(null);
    const feedCallbackRef = useRef(null);

    // Fetch pending connections count
    const fetchPendingConnectionsCount = useCallback(async () => {
        try {
            const response = await ConnectionService.getPendingCount();
            if (response.success) {
                setPendingConnectionsCount(response.data?.pending_count || 0);
            }
        } catch (error) {
            console.error('Error fetching pending connections count:', error);
        }
    }, []);

    // Fetch new feed posts count
    const fetchNewFeedPostsCount = useCallback(async () => {
        try {
            const response = await socialFeedService.getNewPostsCount();
            if (response.success) {
                setNewFeedPostsCount(response.data?.new_count || 0);
            }
        } catch (error) {
            console.error('Error fetching new feed posts count:', error);
        }
    }, []);

    // Mark connections as viewed (clear badge)
    const markConnectionsViewed = useCallback(() => {
        setPendingConnectionsCount(0);
        // Trigger callback if registered (for refreshing connection list)
        if (connectionCallbackRef.current) {
            connectionCallbackRef.current();
        }
    }, []);

    // Mark feed as viewed (clear badge)
    const markFeedViewed = useCallback(() => {
        socialFeedService.markFeedAsViewed();
        setNewFeedPostsCount(0);
        // Trigger callback if registered (for refreshing feed)
        if (feedCallbackRef.current) {
            feedCallbackRef.current();
        }
    }, []);

    // Register callback for connection list refresh
    const registerConnectionCallback = useCallback((callback) => {
        connectionCallbackRef.current = callback;
    }, []);

    // Register callback for feed refresh
    const registerFeedCallback = useCallback((callback) => {
        feedCallbackRef.current = callback;
    }, []);

    // No automatic fetch — counts are loaded on-demand from Lending Network pages only
    // Call fetchPendingConnectionsCount() / fetchNewFeedPostsCount() from SocialFeed or MyNetwork

    const value = {
        // Connection badges
        pendingConnectionsCount,
        setPendingConnectionsCount,
        fetchPendingConnectionsCount,
        markConnectionsViewed,
        registerConnectionCallback,

        // Feed badges
        newFeedPostsCount,
        setNewFeedPostsCount,
        fetchNewFeedPostsCount,
        markFeedViewed,
        registerFeedCallback,
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default NotificationContext;
